import React, { createContext, useContext, useState, useEffect } from "react";
import { API_URL } from "../constant";
import { LoginModal } from "../components/topcoin/LoginModal";
import { CheckCircle2, AlertTriangle } from "lucide-react";

const AppContext = createContext(undefined);

export function AppContextProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoginModalOpen, setLoginModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("success");
  const [cartItems, setCartItems] = useState([]);
  const [purchasedCardIds, setPurchasedCardIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMounted, setIsMounted] = useState(false);

  const apiUrl = API_URL;

  // Load session from local storage
  useEffect(() => {
    setIsMounted(true);
    
    const sessionStr = localStorage.getItem("topcoin_user_session");
    if (sessionStr) {
      try {
        const session = JSON.parse(sessionStr);
        const now = Date.now();
        const thirtyMins = 30 * 60 * 1000;
        
        if (now - session.timestamp < thirtyMins) {
          setUser(session.user);
          
          const remainingTime = thirtyMins - (now - session.timestamp);
          const timer = setTimeout(() => {
            handleLogout("Your session has expired. Please log in again.");
          }, remainingTime);
          
          return () => clearTimeout(timer);
        } else {
          localStorage.removeItem("topcoin_user_session");
        }
      } catch (e) {
        localStorage.removeItem("topcoin_user_session");
      }
    }
  }, []);

  // Fetch cart and purchased cards when user state changes
  useEffect(() => {
    if (user && user.access_token) {
      // Fetch cart
      fetch(`${apiUrl}/api/cart`, {
        headers: {
          "Authorization": `Bearer ${user.access_token}`,
          "Accept": "application/json"
        }
      })
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          setCartItems(data.data);
        }
      })
      .catch(err => console.error("Failed to fetch cart:", err));

      // Fetch purchased cards
      refreshPurchasedCards();
    } else if (isMounted) {
      setCartItems([]);
      setPurchasedCardIds([]);
    }
  }, [user, isMounted, apiUrl]);

  const refreshPurchasedCards = () => {
    if (user && user.access_token) {
      fetch(`${apiUrl}/api/customer/captain-tc-cards`, {
        headers: {
          "Authorization": `Bearer ${user.access_token}`,
          "Accept": "application/json"
        }
      })
      .then(res => res.json())
      .then(data => {
        if (data.cards) {
          const purchasedIds = data.cards.filter((c) => c.purchased).map((c) => String(c.id));
          setPurchasedCardIds(purchasedIds);
        }
      })
      .catch(err => console.error("Failed to fetch purchased cards:", err));
    }
  };

  const showToast = (msg, type = "success") => {
    setToastMessage(msg);
    setToastType(type);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setLoginModalOpen(false);
    
    localStorage.setItem("topcoin_user_session", JSON.stringify({
      user: userData,
      timestamp: Date.now()
    }));

    showToast(`Welcome back, ${userData.name}!`);
    
    setTimeout(() => {
      handleLogout("Your session has expired. Please log in again.");
    }, 30 * 60 * 1000);
  };

  const handleLogout = (msg = "Logged out successfully") => {
    setUser(null);
    setCartItems([]);
    localStorage.removeItem("topcoin_user_session");
    showToast(msg);
  };

  const addToCart = async (item) => {
    if (!user) {
      setLoginModalOpen(true);
      return;
    }

    if (purchasedCardIds.includes(String(item.id))) {
      showToast("You already bought this card!", "warning");
      return;
    }

    if (cartItems.length > 0) {
      showToast("You can only have one Captain TC card in your cart at a time.", "warning");
      return;
    }

    try {
      const res = await fetch(`${apiUrl}/api/cart/add`, {
        method: 'POST',
        headers: {
          "Authorization": `Bearer ${user.access_token}`,
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          card_id: item.id,
          qty: 1
        })
      });
      
      const data = await res.json();
      
      if (!res.ok || data.status === 'error') {
        showToast(data.message || "Failed to add to cart.", "error");
        return;
      }
      
      setCartItems(data.data);
      showToast(`Added ${item.name} to cart!`);
    } catch (err) {
      console.error("Failed to add to cart:", err);
      showToast("An error occurred.", "error");
    }
  };

  const updateCartQty = (id, qty) => {
    if (qty < 1) return;
    if (qty > 1) {
      showToast("You can only buy 1 card of this type.", "warning");
      return;
    }
    
    // Optimistic UI update
    setCartItems((prev) => prev.map((i) => (String(i.id) === String(id) ? { ...i, qty } : i)));

    if (user && user.access_token) {
      fetch(`${apiUrl}/api/cart/update`, {
        method: 'PUT',
        headers: {
          "Authorization": `Bearer ${user.access_token}`,
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          card_id: id,
          qty: qty
        })
      }).catch(err => console.error("Failed to update cart:", err));
    }
  };

  const removeFromCart = (id) => {
    // Optimistic UI update
    setCartItems((prev) => prev.filter((i) => String(i.id) !== String(id)));

    if (user && user.access_token) {
      fetch(`${apiUrl}/api/cart/remove`, {
        method: 'DELETE',
        headers: {
          "Authorization": `Bearer ${user.access_token}`,
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          card_id: id
        })
      }).catch(err => console.error("Failed to remove from cart:", err));
    }
  };

  const clearCart = () => {
    setCartItems([]);
  };

  return (
    <AppContext.Provider
      value={{
        user,
        isLoginModalOpen,
        setLoginModalOpen,
        toastMessage,
        toastType,
        showToast,
        cartItems,
        addToCart,
        updateCartQty,
        removeFromCart,
        handleLoginSuccess,
        handleLogout,
        searchQuery,
        setSearchQuery,
        clearCart,
        refreshPurchasedCards
      }}
    >
      {children}
      {toastMessage && (
        <div 
          className="fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-2xl border flex items-center gap-3" 
          style={{ 
            position: 'fixed', 
            top: '1rem', 
            right: '1rem', 
            zIndex: 50, 
            padding: '0.75rem 1.25rem', 
            borderRadius: '0.5rem', 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 
            border: `1px solid ${toastType === "warning" ? '#f59e0b' : '#fbbf24'}`, 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            backgroundColor: toastType === "warning" ? '#fef3c7' : '#03173d',
            color: toastType === "warning" ? '#78350f' : '#fff'
          }}
        >
          {toastType === "warning" ? (
            <AlertTriangle className="w-5 h-5 text-amber-600" style={{ width: '1.25rem', height: '1.25rem', color: '#d97706' }} />
          ) : (
            <CheckCircle2 className="w-5 h-5 text-amber-400" style={{ width: '1.25rem', height: '1.25rem', color: '#fbbf24' }} />
          )}
          <span className="text-sm font-bold" style={{ fontSize: '0.875rem', fontWeight: 700 }}>{toastMessage}</span>
        </div>
      )}
      <LoginModal 
        isOpen={isLoginModalOpen} 
        onClose={() => setLoginModalOpen(false)} 
        onLoginSuccess={handleLoginSuccess} 
      />
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppContextProvider");
  }
  return context;
}
